import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { RoomInspectorStore } from '../../stores/roomInspectorStore';

const threshold = 120;
interface ScrollAnchor {
  sessionId: string;
  messageId?: string;
  offset: number;
  top: number;
}
function messages(list: HTMLElement) {
  return Array.from(
    list.querySelectorAll<HTMLElement>(
      '[data-message-id], [data-transcript-message-id]',
    ),
  );
}
function messageId(node: HTMLElement) {
  return node.dataset.messageId || node.dataset.transcriptMessageId;
}
function capture(list: HTMLElement, sessionId: string): ScrollAnchor {
  const rect = list.getBoundingClientRect();
  const node = messages(list).find((item) => {
    const box = item.getBoundingClientRect();
    return (
      item.getClientRects().length > 0 &&
      box.bottom > rect.top &&
      box.top < rect.bottom
    );
  });
  return {
    sessionId,
    messageId: node && messageId(node),
    offset: node ? node.getBoundingClientRect().top - rect.top : 0,
    top: list.scrollTop,
  };
}

export function useTranscriptHistory(store: RoomInspectorStore) {
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchor = useRef<ScrollAnchor | null>(null);
  const readingAnchor = useRef<ScrollAnchor | null>(null);
  const request = useRef<symbol | null>(null);
  const active = useRef('');
  const positioned = useRef('');
  const previousTop = useRef(0);
  const hidden = useRef(false);
  const touchY = useRef<number | null>(null);
  const snapshot = store.snapshot;
  const sessionId = snapshot?.session.agentSessionId ?? '';
  const loadMore = useCallback(
    async (retry = false) => {
      const list = listRef.current;
      if (
        !list ||
        !sessionId ||
        request.current ||
        store.historyLoading ||
        (!retry && store.sessionError) ||
        store.snapshot?.session.agentSessionId !== sessionId ||
        !store.snapshot.historyNextCursor
      )
        return;
      const token = Symbol();
      request.current = token;
      anchor.current = capture(list, sessionId);
      try {
        await store.moreMessages(3);
      } finally {
        if (request.current === token) request.current = null;
      }
    },
    [store, sessionId],
  );

  useLayoutEffect(() => {
    const list = listRef.current;
    if (active.current !== sessionId) {
      active.current = sessionId;
      positioned.current = '';
      request.current = null;
      anchor.current = null;
      readingAnchor.current = null;
    }
    if (!list || !sessionId) return;
    if (list.clientHeight === 0) {
      hidden.current = true;
      return;
    }
    if (positioned.current !== sessionId) {
      list.scrollTop = 0;
      positioned.current = sessionId;
    } else if (hidden.current) {
      list.scrollTop = previousTop.current;
    }
    hidden.current = false;
    if (!store.historyLoading && anchor.current?.sessionId === sessionId) {
      const saved = anchor.current;
      const node = messages(list).find(
        (item) =>
          messageId(item) === saved.messageId &&
          item.getClientRects().length > 0,
      );
      if (node)
        list.scrollTop +=
          node.getBoundingClientRect().top -
          list.getBoundingClientRect().top -
          saved.offset;
      else list.scrollTop = saved.top;
      anchor.current = null;
    }
    previousTop.current = list.scrollTop;
    readingAnchor.current = capture(list, sessionId);
  });

  useEffect(() => {
    if (sessionId) void loadMore();
  }, [sessionId, loadMore]);
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const observer = new ResizeObserver(() => {
      if (list.clientHeight > 0 && positioned.current !== sessionId) {
        list.scrollTop = 0;
        positioned.current = sessionId;
        previousTop.current = list.scrollTop;
      } else if (list.clientHeight > 0 && hidden.current) {
        list.scrollTop = previousTop.current;
        hidden.current = false;
      } else if (
        list.clientHeight > 0 &&
        readingAnchor.current?.sessionId === sessionId
      ) {
        // Markdown, images and diagrams may settle after the page has rendered.
        // Keep the same visible message in place when that changes content height.
        const saved = readingAnchor.current;
        const node = messages(list).find(
          (item) =>
            messageId(item) === saved.messageId &&
            item.getClientRects().length > 0,
        );
        if (node)
          list.scrollTop +=
            node.getBoundingClientRect().top -
            list.getBoundingClientRect().top -
            saved.offset;
      }
      if (list.clientHeight > 0) {
        previousTop.current = list.scrollTop;
        readingAnchor.current = capture(list, sessionId);
      }
    });
    observer.observe(list);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [sessionId]);

  return {
    listRef,
    contentRef,
    loadMore,
    onScroll: () => {
      const list = listRef.current;
      if (!list || !list.clientHeight) return;
      if (
        store.historyLoading &&
        anchor.current &&
        list.scrollTop !== previousTop.current
      )
        anchor.current = capture(list, sessionId);
      readingAnchor.current = capture(list, sessionId);
      const downward = list.scrollTop > previousTop.current;
      previousTop.current = list.scrollTop;
      if (
        downward &&
        list.scrollHeight - list.clientHeight - list.scrollTop <= threshold
      )
        void loadMore();
    },
    onWheel: (event: React.WheelEvent<HTMLDivElement>) => {
      if (
        event.deltaY > 0 &&
        listRef.current &&
        listRef.current.scrollHeight -
          listRef.current.clientHeight -
          listRef.current.scrollTop <=
          threshold
      )
        void loadMore();
    },
    onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => {
      touchY.current = event.touches[0]?.clientY ?? null;
    },
    onTouchMove: (event: React.TouchEvent<HTMLDivElement>) => {
      const y = event.touches[0]?.clientY;
      if (
        y !== undefined &&
        touchY.current !== null &&
        y < touchY.current &&
        listRef.current &&
        listRef.current.scrollHeight -
          listRef.current.clientHeight -
          listRef.current.scrollTop <=
          threshold
      )
        void loadMore();
      touchY.current = y ?? null;
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        event.target === event.currentTarget &&
        ['End', 'PageDown', 'ArrowDown'].includes(event.key) &&
        listRef.current &&
        (event.key === 'End' ||
          listRef.current.scrollHeight -
            listRef.current.clientHeight -
            listRef.current.scrollTop <=
            threshold)
      )
        void loadMore();
    },
  };
}
